import type { AxiosInstance } from 'axios';
import { ExceptionType, Result } from 'services/Result';
import { injectable } from 'inversify';
import { IGame, IGameResponse } from 'interfaces/api/IGame';
import { LogService } from 'services/LogService';
import { ILeague } from 'interfaces/api/ILeague';
import { HidePitchLocationRequest, HidePitchLocationResponse } from 'interfaces/api/IHidePitchLocation';

export interface IGamesService {
  getCurrentGames(dispatch: any): Promise<IGameResponse>;
  getGame(id: string, isScheduledGame: boolean, useSharingSharingNetworkRules: boolean): Promise<IGame>;
  getLeagueById(id: string): Promise<ILeague>;
  calculateHidePitchLocation(sessionId: string, sessionType: string, externalSessionId: string, homeTeamId: string, awayTeamId: string): Promise<HidePitchLocationResponse>;
}

@injectable()
export class GamesService implements IGamesService {
  private axiosInstance: AxiosInstance;

  private logService: LogService;

  constructor(axiosInstance: AxiosInstance, logService: LogService) {
    this.axiosInstance = axiosInstance;
    this.logService = logService;
  }

  getCurrentGames = async (useSharingSharingNetworkRules: boolean): Promise<IGameResponse> => {
    try {
      const response = await this.axiosInstance.get<Result<IGameResponse>>(`games/?fromSharingNetwork=${useSharingSharingNetworkRules}`);
      if (response && response.data && response.data.errors && response.data.errors.length > 0) {
        for (let i = 0; i < response.data.errors.length; i++) {
          if (response.data.errors[i].error_details._exception_type === ExceptionType.MissingOrganizationException) {
            const error = new Error('Missing organization exception');
            error.cause = ExceptionType.MissingOrganizationException;
            throw error;
          }
        }
      }
      if (response.data && !response.data.isError && response.data.data) {
        return response.data.data;
      }
      throw Error(JSON.stringify(response));
    } catch (err) {
      this.logService.logError({ error: err, message: `Exception when getting current games` });
      throw err;
    }
  };

  getGame = async (id: string, isScheduledGame: boolean, useSharingSharingNetworkRules: boolean): Promise<IGame> => {
    try {
      const response = await this.axiosInstance.get<Result<IGame>>(
        `games/${id}/?isScheduledGame=${isScheduledGame}&fromSharingNetwork=${useSharingSharingNetworkRules}`
      );
      if (response.data && !response.data.isError && response.data.data) {
        return response.data.data;
      }
      throw Error(JSON.stringify(response));
    } catch (err) {
      this.logService.logError({ error: err, message: `Exception when getting game.` });
      throw Error(`Failed to fetch game:${id}`);
    }
  };

  getLeagueById = async (id: string) => {
    try {
      const response = await this.axiosInstance.get<Result<IGame>>(`contextual/leagues/${id}`);
      if (response.data && !response.data.isError && response.data.data) {
        return response.data.data;
      }

      if (response.status === 404) {
        throw Error('Failed to fetch league: 404');
      }

      throw Error(JSON.stringify(response));
    } catch (err) {
      this.logService.logError({ error: err, message: `Exception when fetching league.` });
      throw Error(`Failed to fetch league: ${id}`);
    }
  };

  calculateHidePitchLocation = async (sessionId: string, sessionType: string, externalSessionId: string, homeTeamId: string, awayTeamId: string) => {
    try {
      const payload: HidePitchLocationRequest = {
        sessionId: sessionId,
        sessionType: sessionType,
        externalSessionId: externalSessionId,
        homeTeamId: homeTeamId,
        awayTeamId: awayTeamId,
      };

      const response = await this.axiosInstance.post<Result<HidePitchLocationResponse>>(`games/calculateHidePitchLocation`, payload);
      if (response.data && !response.data.isError && response.data.data) {
        return response.data.data;
      }

      throw Error(JSON.stringify(response));
    } catch (err) {
      this.logService.logError({ error: err, message: `Exception when fetching league.` });
      throw Error(`Failed to fetch hide pitch location`);
    }
  };
}
